import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { fetchEventStatus, toggleAttended, toggleCaught, resolveEventStatus } from '../attendedUtils';

function EventDetail({ slugOverride } = {}) {
    const params = useParams();
    const slug = slugOverride || params.slug;
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Attended / caught state
    const [attended, setAttended] = useState(false);
    const [performed, setPerformed] = useState(false);
    const [worked, setWorked] = useState(false);
    const [artistStatus, setArtistStatus] = useState({});
    const [attendedLoading, setAttendedLoading] = useState(false);
    const [caughtLoading, setCaughtLoading] = useState({});

    const loadEventStatus = useCallback(async (eventId) => {
        try {
            const status = await fetchEventStatus(eventId);
            setAttended(status.attended);
            setPerformed(status.performed || false);
            setWorked(status.worked || false);
            setArtistStatus(status.artist_status || {});
        } catch (e) {
            // Not logged in or error — buttons just won't show status
        }
    }, []);

    // Footnote editing state
    const [allArtists, setAllArtists] = useState([]);
    const [footnotes, setFootnotes] = useState([]);
    const [editingFootnotes, setEditingFootnotes] = useState(false);
    const [draftFootnotes, setDraftFootnotes] = useState([]);
    const [savingFootnotes, setSavingFootnotes] = useState(false);
    const [footnoteError, setFootnoteError] = useState(null);

    useEffect(() => {
        Promise.all([
            axios.get(`${config.API_URL}/events/${slug}`),
            axios.get(`${config.API_URL}/artists`),
        ])
            .then(([eventRes, artistsRes]) => {
                setData(eventRes.data);
                setFootnotes(eventRes.data.footnotes || []);
                setAllArtists(artistsRes.data);
                setLoading(false);
                // Load attended status once we have the event id
                if (eventRes.data.event) {
                    loadEventStatus(eventRes.data.event.id);
                }

                // Build dynamic page title
                const ev = eventRes.data.event;
                const arts = eventRes.data.artists || [];
                const headliner = arts.find(a => a.role === 'headliner');
                if (ev) {
                    const dateStr = ev.event_date
                        ? new Date(ev.event_date).toISOString().slice(0, 10).replace(/-/g, '')
                        : null;
                    const parts = [dateStr, ev.title || null, ev.venue_name, headliner ? headliner.artist_name : null]
                        .filter(Boolean);
                    document.title = `SHARC - ${parts.join(' - ')}`;
                }
            })
            .catch(() => {
                setError('Failed to load event');
                setLoading(false);
            });
        return () => { document.title = 'SHARC - Shows Archive'; };
    }, [slug, loadEventStatus]);

    // --- Footnote helpers ---
    const startEditingFootnotes = () => {
        setDraftFootnotes(footnotes.map(fn => ({
            description: fn.description,
            url: fn.url || '',
            artist_id: fn.artist_id ? String(fn.artist_id) : '',
        })));
        setFootnoteError(null);
        setEditingFootnotes(true);
    };

    const cancelEditingFootnotes = () => {
        setEditingFootnotes(false);
        setFootnoteError(null);
    };

    const addFootnote = () => {
        setDraftFootnotes(prev => [...prev, { description: '', url: '', artist_id: '' }]);
    };

    const updateFootnote = (index, field, value) => {
        setDraftFootnotes(prev => prev.map((fn, i) =>
            i === index ? { ...fn, [field]: value } : fn
        ));
    };

    const removeFootnote = (index) => {
        setDraftFootnotes(prev => prev.filter((_, i) => i !== index));
    };

    const handleToggleAttended = async () => {
        if (attendedLoading || !data) return;
        setAttendedLoading(true);
        try {
            const result = await toggleAttended(data.event.id);
            setAttended(result.attended);
            setPerformed(result.performed || false);
            setWorked(result.worked || false);
            if (!result.attended) {
                setArtistStatus({});
            } else if (result.artist_status) {
                setArtistStatus(result.artist_status);
            }
        } catch (e) {
            console.error('toggleAttended error:', e);
        } finally {
            setAttendedLoading(false);
        }
    };

    const handleToggleCaught = async (artistId) => {
        if (caughtLoading[artistId] || !data) return;
        setCaughtLoading(prev => ({ ...prev, [artistId]: true }));
        try {
            const result = await toggleCaught(data.event.id, artistId);
            setArtistStatus(prev => ({ ...prev, [artistId]: result.caught }));
        } catch (e) {
            console.error('toggleCaught error:', e);
        } finally {
            setCaughtLoading(prev => ({ ...prev, [artistId]: false }));
        }
    };

    const saveFootnotes = async () => {
        if (draftFootnotes.some(fn => !fn.description.trim())) {
            setFootnoteError('Each footnote must have a description.');
            return;
        }
        setSavingFootnotes(true);
        setFootnoteError(null);
        try {
            const payload = {
                footnotes: draftFootnotes
                    .filter(fn => fn.description.trim())
                    .map(fn => ({
                        description: fn.description.trim(),
                        url: fn.url.trim() || null,
                        artist_id: fn.artist_id ? parseInt(fn.artist_id) : null,
                    }))
            };
            // Use numeric event ID for PUT (fetched from data)
            const eventId = data && data.event ? data.event.id : slug;
            const res = await axios.put(`${config.API_URL}/events/${eventId}/footnotes`, payload);
            setFootnotes(res.data.footnotes || []);
            setEditingFootnotes(false);
        } catch (err) {
            setFootnoteError('Failed to save footnotes. Please try again.');
        } finally {
            setSavingFootnotes(false);
        }
    };

    if (loading) return <div style={styles.message}>Loading event...</div>;
    if (error) return <div style={styles.error}>{error}</div>;
    if (!data) return null;

    const { event, artists, number_of_artists, promoters } = data;

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-US', {
            weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC'
        });
    };

    const headliner = artists.find(a => a.role === 'headliner');

    const getRoleBadgeStyle = (role) => ({
        ...styles.roleBadge,
        backgroundColor: role === 'headliner' ? '#bb86fc22' : '#2c2c2c',
        borderColor: role === 'headliner' ? '#bb86fc' : '#444',
        color: role === 'headliner' ? '#bb86fc' : '#aaa',
    });

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <Link to="/events" style={styles.backLink}>← Back to Events</Link>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    {resolveEventStatus(performed, worked, attended) === 'performed' ? (
                        <span style={styles.performedBadge}>Performed</span>
                    ) : resolveEventStatus(performed, worked, attended) === 'worked' ? (
                        <span style={styles.workedBadge}>Worked</span>
                    ) : (
                        <button
                            onClick={handleToggleAttended}
                            disabled={attendedLoading}
                            style={attended ? styles.attendedBtnOn : styles.attendedBtnOff}
                        >
                            {attendedLoading ? '…' : attended ? '✓ Attended' : 'Mark Attended'}
                        </button>
                    )}
                    <Link to={`/events/${event.slug || event.id}/edit`} style={styles.editButton}>Edit Event</Link>
                </div>
            </div>

            {/* Event Header */}
            <div style={styles.header}>
                <div style={event.image_url ? styles.headerWithImage : undefined}>
                    {event.image_url && (
                        <img
                            src={event.image_url}
                            alt={event.title || (headliner ? headliner.artist_name : 'Event poster')}
                            style={styles.eventImage}
                            onError={e => { e.target.style.display = 'none'; }}
                        />
                    )}
                    <div style={styles.headerText}>
                        <h1 style={styles.title}>
                            {headliner ? headliner.artist_name : 'Event'}
                        </h1>
                        {event.title && (
                            <div style={styles.eventTitle}>{event.title}</div>
                        )}
                        <div style={styles.dateText}>{formatDate(event.event_date)}</div>
                        <div style={styles.venueText}>
                            <Link to={`/venues/${event.venue_slug || event.venue_id}`} style={styles.venueLink}>
                                {event.venue_name}
                            </Link>
                            <span style={styles.venueLocation}>
                                {' · '}{[event.venue_city, event.venue_state, event.venue_country].filter(Boolean).join(', ')}
                            </span>
                        </div>
                        {event.stage_name && (
                            <div style={styles.stageText}>
                                {event.stage_name}
                            </div>
                        )}
                        {event.festival_id && event.festival_name && (
                            <div style={styles.festivalText}>
                                <Link to={`/festivals/${event.festival_slug}`} style={styles.festivalLink}>{event.festival_name}</Link>
                            </div>
                        )}
                        {promoters && promoters.length > 0 && (
                            <div style={styles.promoterText}>
                                {promoters.map((p, i) => (
                                    <span key={p.id}>
                                        <Link to={`/promoters/${p.slug}`} style={styles.promoterLink}>{p.name}</Link>
                                        {i < promoters.length - 1 && ', '}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Key Stats */}
            <div style={styles.statsGrid}>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{number_of_artists}</div>
                    <div style={styles.statLabel}>Artists</div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{event.venue_capacity ? event.venue_capacity.toLocaleString() : '—'}</div>
                    <div style={styles.statLabel}>Venue Capacity</div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{event.stage_capacity ? event.stage_capacity.toLocaleString() : '—'}</div>
                    <div style={styles.statLabel}>Stage Capacity</div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{event.est_attendance || '—'}</div>
                    <div style={styles.statLabel}>Est. Attendance</div>
                </div>
                {event.est_attendance_number > 0 && (
                    <div style={styles.statCard}>
                        <div style={styles.statValue}>~{event.est_attendance_number.toLocaleString()}</div>
                        <div style={styles.statLabel}>Est. People</div>
                    </div>
                )}
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{event.age_restriction || '—'}</div>
                    <div style={styles.statLabel}>Age Restriction</div>
                </div>
            </div>

            {/* Lineup */}
            <div style={styles.section}>
                <h2 style={styles.sectionTitle}>Lineup</h2>
                <div style={styles.lineupList}>
                    {artists.map((artist, index) => (
                        <div key={artist.id} style={styles.lineupCard}>
                            <div style={styles.lineupPosition}>
                                {artist.role === 'headliner' ? '★' : `#${artists.length - index}`}
                            </div>
                            <div style={styles.lineupInfo}>
                                <Link to={`/artists/${artist.artist_slug || artist.artist_id}`} style={styles.artistLink}>
                                    {artist.artist_name}
                                </Link>
                                <div style={styles.artistLocation}>
                                    {[artist.artist_city, artist.artist_state, artist.artist_country]
                                        .filter(Boolean).join(', ')}
                                </div>
                            </div>
                            <span style={getRoleBadgeStyle(artist.role)}>
                                {artist.role === 'headliner' ? 'Headliner' : 'Support'}
                            </span>
                            {attended && !worked && (
                                <button
                                    onClick={() => handleToggleCaught(artist.artist_id)}
                                    disabled={!!caughtLoading[artist.artist_id]}
                                    style={artistStatus[artist.artist_id] === true
                                        ? styles.caughtBtnOn
                                        : artistStatus[artist.artist_id] === false
                                        ? styles.caughtBtnMissed
                                        : styles.caughtBtnOff}
                                >
                                    {caughtLoading[artist.artist_id] ? '…'
                                        : artistStatus[artist.artist_id] === true ? '✓ Caught This'
                                        : artistStatus[artist.artist_id] === false ? '✕ Missed This'
                                        : 'Caught This?'}
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Venue Info */}
            <div style={styles.section}>
                <h2 style={styles.sectionTitle}>Venue Details</h2>
                <div style={styles.venueCard}>
                    <div style={styles.venueCardName}>
                        <Link to={`/venues/${event.venue_slug || event.venue_id}`} style={styles.venueLink}>
                            {event.venue_name}
                        </Link>
                    </div>
                    {event.venue_street && (
                        <div style={styles.venueCardDetail}>{event.venue_street}</div>
                    )}
                    <div style={styles.venueCardDetail}>
                        {[event.venue_city, event.venue_state, event.venue_country].filter(Boolean).join(', ')}
                    </div>
                    <div style={styles.venueCardMeta}>
                        <span style={styles.tag}>Capacity: {event.venue_capacity ? event.venue_capacity.toLocaleString() : '—'}</span>
                        {event.age_restriction && <span style={styles.tag}>{event.age_restriction}</span>}
                    </div>
                </div>
            </div>

            {/* Footnotes */}
            <div style={styles.section}>
                <div style={styles.sectionHeaderRow}>
                    <h2 style={styles.sectionTitle}>Footnotes</h2>
                    {!editingFootnotes && (
                        <button onClick={startEditingFootnotes} style={styles.editSectionButton}>
                            {footnotes.length > 0 ? 'Edit Footnotes' : '+ Add Footnotes'}
                        </button>
                    )}
                </div>

                {/* Read mode */}
                {!editingFootnotes && (
                    footnotes.length === 0 ? (
                        <div style={styles.emptyText}>No footnotes for this event.</div>
                    ) : (
                        <div style={styles.footnoteList}>
                            {footnotes.map((fn, index) => (
                                <div key={fn.id} style={styles.footnoteCard}>
                                    <div style={styles.footnoteNumber}>{index + 1}</div>
                                    <div style={styles.footnoteContent}>
                                        <div style={styles.footnoteDescription}>{fn.description}</div>
                                        <div style={styles.footnoteMeta}>
                                            {fn.artist_name && (
                                                <Link
                                                    to={`/artists/${fn.artist_slug || fn.artist_id}`}
                                                    style={styles.footnoteArtistLink}
                                                >
                                                    {fn.artist_name}
                                                </Link>
                                            )}
                                            {fn.url && (
                                                <a
                                                    href={fn.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={styles.footnoteUrlLink}
                                                >
                                                    Source ↗
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )
                )}

                {/* Edit mode */}
                {editingFootnotes && (
                    <div style={styles.editBox}>
                        {footnoteError && (
                            <div style={styles.footnoteErrorMsg}>{footnoteError}</div>
                        )}

                        {draftFootnotes.length === 0 && (
                            <div style={styles.emptyText}>No footnotes yet. Click "+ Add Footnote" to add one.</div>
                        )}

                        {draftFootnotes.map((fn, index) => (
                            <div key={index} style={styles.footnoteEditCard}>
                                <div style={styles.footnoteEditHeader}>
                                    <span style={styles.footnoteEditNumber}>#{index + 1}</span>
                                    <button
                                        onClick={() => removeFootnote(index)}
                                        style={styles.removeFootnoteButton}
                                    >✕ Remove</button>
                                </div>

                                <div style={styles.footnoteEditField}>
                                    <label style={styles.footnoteEditLabel}>Description *</label>
                                    <input
                                        type="text"
                                        value={fn.description}
                                        onChange={e => updateFootnote(index, 'description', e.target.value)}
                                        placeholder="e.g. First time these two artists performed together"
                                        style={styles.footnoteInput}
                                    />
                                </div>

                                <div style={styles.footnoteEditRow}>
                                    <div style={{ flex: 1 }}>
                                        <label style={styles.footnoteEditLabel}>URL (optional)</label>
                                        <input
                                            type="text"
                                            value={fn.url}
                                            onChange={e => updateFootnote(index, 'url', e.target.value)}
                                            placeholder="https://..."
                                            style={styles.footnoteInput}
                                        />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label style={styles.footnoteEditLabel}>Referenced Artist (optional)</label>
                                        <select
                                            value={fn.artist_id}
                                            onChange={e => updateFootnote(index, 'artist_id', e.target.value)}
                                            style={styles.footnoteInput}
                                        >
                                            <option value="">No artist</option>
                                            {allArtists.map(a => (
                                                <option key={a.id} value={a.id}>
                                                    {a.name}{a.city ? ` — ${a.city}` : ''}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        ))}

                        <div style={styles.footnoteEditActions}>
                            <button onClick={addFootnote} style={styles.addFootnoteButton}>+ Add Footnote</button>
                            <div style={styles.footnoteEditButtons}>
                                <button onClick={cancelEditingFootnotes} style={styles.cancelButton}>Cancel</button>
                                <button
                                    onClick={saveFootnotes}
                                    disabled={savingFootnotes}
                                    style={styles.saveButton}
                                >
                                    {savingFootnotes ? 'Saving...' : 'Save Footnotes'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

        </div>
    );
}

const styles = {
    attendedBtnOff: { backgroundColor: 'transparent', color: '#aaa', border: '1px solid #555', borderRadius: '6px', padding: '7px 16px', fontSize: '14px', cursor: 'pointer', fontWeight: '500', whiteSpace: 'nowrap' },
    attendedBtnOn: { backgroundColor: '#1a3a1a', color: '#66bb6a', border: '1px solid #66bb6a', borderRadius: '6px', padding: '7px 16px', fontSize: '14px', cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap' },
    performedBadge: { backgroundColor: '#1a2a3a', color: '#64b5f6', border: '1px solid #64b5f6', borderRadius: '6px', padding: '7px 16px', fontSize: '14px', fontWeight: 'bold', whiteSpace: 'nowrap' },
    workedBadge: { backgroundColor: '#2b1a0d', color: '#ff9800', border: '1px solid #7a4200', borderRadius: '6px', padding: '7px 16px', fontSize: '14px', fontWeight: 'bold', whiteSpace: 'nowrap' },
    caughtBtnOff: { backgroundColor: 'transparent', color: '#888', border: '1px solid #444', borderRadius: '4px', padding: '4px 12px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap', marginLeft: '8px' },
    caughtBtnOn: { backgroundColor: '#1a3a1a', color: '#66bb6a', border: '1px solid #66bb6a', borderRadius: '4px', padding: '4px 12px', fontSize: '12px', cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap', marginLeft: '8px' },
    caughtBtnMissed: { backgroundColor: '#2c1a1e', color: '#e57373', border: '1px solid #e57373', borderRadius: '4px', padding: '4px 12px', fontSize: '12px', cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap', marginLeft: '8px' },
    backLink: { color: '#bb86fc', textDecoration: 'none', fontSize: '14px', display: 'inline-block', marginBottom: '24px' },
    editButton: { backgroundColor: '#2c2c2c', color: '#bb86fc', border: '1px solid #bb86fc', borderRadius: '4px', padding: '6px 16px', fontSize: '14px', textDecoration: 'none', fontWeight: 'bold' },
    header: { marginBottom: '32px' },
    headerWithImage: { display: 'flex', gap: '24px', alignItems: 'flex-start', marginBottom: '8px' },
    headerText: { flex: 1 },
    eventImage: { width: '120px', height: '160px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #333', flexShrink: 0 },
    title: { fontSize: '40px', fontWeight: 'bold', color: '#e0e0e0', marginBottom: '4px' },
    eventTitle: { fontSize: '18px', color: '#aaa', fontStyle: 'italic', marginBottom: '8px' },
    dateText: { fontSize: '20px', color: '#bb86fc', marginBottom: '8px', fontWeight: '500' },
    venueText: { fontSize: '16px', color: '#aaa' },
    venueLink: { color: '#bb86fc', textDecoration: 'none' },
    venueLocation: { color: '#aaa' },
    stageText: { fontSize: '14px', color: '#888', marginTop: '4px' },
    festivalText: { fontSize: '14px', color: '#aaa', marginTop: '6px' },
    festivalLink: { color: '#bb86fc', textDecoration: 'none', fontWeight: 'bold' },
    promoterText: { fontSize: '14px', color: '#aaa', marginTop: '4px' },
    promoterLink: { color: '#bb86fc', textDecoration: 'none' },
    statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '16px', marginBottom: '40px' },
    statCard: { backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '20px', textAlign: 'center' },
    statValue: { fontSize: '28px', fontWeight: 'bold', color: '#bb86fc', marginBottom: '8px' },
    statLabel: { fontSize: '13px', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.5px' },
    section: { marginBottom: '48px' },
    sectionHeaderRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', paddingBottom: '8px', borderBottom: '1px solid #333' },
    sectionTitle: { fontSize: '22px', fontWeight: 'bold', color: '#e0e0e0', margin: 0 },
    editSectionButton: { backgroundColor: '#2c2c2c', color: '#bb86fc', border: '1px solid #bb86fc', borderRadius: '4px', padding: '5px 14px', fontSize: '13px', cursor: 'pointer', fontWeight: 'bold' },
    // Lineup
    lineupList: { display: 'flex', flexDirection: 'column', gap: '12px' },
    lineupCard: { backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '16px' },
    lineupPosition: { fontSize: '20px', color: '#bb86fc', fontWeight: 'bold', minWidth: '32px', textAlign: 'center' },
    lineupInfo: { flex: 1 },
    artistLink: { color: '#e0e0e0', textDecoration: 'none', fontSize: '18px', fontWeight: 'bold', display: 'block', marginBottom: '4px' },
    artistLocation: { fontSize: '13px', color: '#aaa' },
    roleBadge: { border: '1px solid', borderRadius: '4px', padding: '4px 10px', fontSize: '12px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' },
    // Footnotes - read mode
    emptyText: { color: '#666', fontSize: '14px', fontStyle: 'italic', padding: '8px 0' },
    footnoteList: { display: 'flex', flexDirection: 'column', gap: '12px' },
    footnoteCard: { backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '16px 20px', display: 'flex', gap: '16px', alignItems: 'flex-start' },
    footnoteNumber: { fontSize: '13px', color: '#666', fontWeight: 'bold', minWidth: '20px', paddingTop: '2px' },
    footnoteContent: { flex: 1 },
    footnoteDescription: { fontSize: '15px', color: '#e0e0e0', marginBottom: '8px', lineHeight: '1.5' },
    footnoteMeta: { display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' },
    footnoteArtistLink: { color: '#bb86fc', textDecoration: 'none', fontSize: '13px', fontWeight: 'bold', backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '3px 10px' },
    footnoteUrlLink: { color: '#bb86fc', textDecoration: 'none', fontSize: '13px', fontWeight: 'bold' },
    // Footnotes - edit mode
    editBox: { backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px', padding: '24px' },
    footnoteErrorMsg: { backgroundColor: '#2c1a1e', border: '1px solid #cf6679', borderRadius: '4px', padding: '10px 14px', color: '#cf6679', marginBottom: '16px', fontSize: '14px' },
    footnoteEditCard: { backgroundColor: '#1e1e1e', border: '1px solid #3a3a3a', borderRadius: '8px', padding: '16px', marginBottom: '12px' },
    footnoteEditHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' },
    footnoteEditNumber: { fontSize: '13px', color: '#666', fontWeight: 'bold' },
    removeFootnoteButton: { backgroundColor: 'transparent', color: '#cf6679', border: '1px solid #cf6679', borderRadius: '4px', padding: '4px 10px', fontSize: '12px', cursor: 'pointer' },
    footnoteEditField: { marginBottom: '12px' },
    footnoteEditRow: { display: 'flex', gap: '12px' },
    footnoteEditLabel: { display: 'block', fontSize: '12px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' },
    footnoteInput: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '8px 12px', fontSize: '14px', color: '#e0e0e0', width: '100%', boxSizing: 'border-box' },
    footnoteEditActions: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' },
    addFootnoteButton: { backgroundColor: '#2c2c2c', color: '#bb86fc', border: '1px solid #bb86fc', borderRadius: '4px', padding: '7px 14px', fontSize: '13px', cursor: 'pointer', fontWeight: 'bold' },
    footnoteEditButtons: { display: 'flex', gap: '10px' },
    cancelButton: { backgroundColor: 'transparent', color: '#aaa', border: '1px solid #444', borderRadius: '4px', padding: '8px 20px', fontSize: '14px', cursor: 'pointer' },
    saveButton: { backgroundColor: '#bb86fc', color: '#121212', border: 'none', borderRadius: '4px', padding: '8px 20px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' },
    // Venue
    venueCard: { backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '24px' },
    venueCardName: { fontSize: '20px', fontWeight: 'bold', marginBottom: '8px' },
    venueCardDetail: { fontSize: '14px', color: '#aaa', marginBottom: '4px' },
    venueCardMeta: { display: 'flex', gap: '8px', marginTop: '16px', flexWrap: 'wrap' },
    tag: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '4px 10px', fontSize: '13px', color: '#bb86fc' },
    message: { textAlign: 'center', color: '#aaa', fontSize: '16px', padding: '60px 0' },
    error: { textAlign: 'center', color: '#cf6679', fontSize: '16px', padding: '60px 0' },
};

export default EventDetail;